import { cn } from '@nexelem/utils';
import { type PropsWithChildren, memo, type RefObject, useState } from 'react';
import { NodeBase } from '../NodeBase';
import { CUSTOM_ICONS } from '@nexelem/icons';
import { Badge, DropdownMenu, Popover, Status } from '@nexelem/components';
import { NodeType, type QueueBatchNode } from '../../types';
import { GanttMode, useProductionPlanQueryParams } from '../../../hooks';
import { isSearchEmphasizedSelector, isSearchSelectedSelector, useProductionPlanSearchStore } from '../../../store';
import { useGanttDraggable, useOnNodeClick } from '../../hooks';
import { MultipleTableAvatar } from '../../../../../molecules/MultipleTableAvatar';
import clsx from 'clsx';
import { GanttUtils } from '../../../utils';
import { GanttConfig } from '../../GanttConfig';
import { useIsGanttNodeDraggable } from '../../utils';
import { TaskMainValueOptions, useProductionPlanSettings } from '../../../components';
import { useNodeShadowParams } from '../NodeSingle/useNodeShadowParams';
import { TaskStatus } from '@nexelem/graphne-api';
import { useIsGanttSimulation } from '../../../GanttSimulation/hooks';
import { useNodeBatchFlagsMapper } from '../../mappers';
import { NodeFlags } from '../NodeFlags';
import { MultipleNodeDropdownMenuContent } from '../NodeMultiple/MultipleNodeDropdownMenuContent';
import { NodeBatchPopoverContent, useCursorHoverState } from '../NodePopover';

export type NodeMultipleData = {
  nodeIds?: Array<string | number>;
};

type NodeBatchProps = {
  node: QueueBatchNode;
};

export const NodeBatch = memo(({ node, children }: PropsWithChildren<NodeBatchProps>) => {
  const [isOpen, setOpen] = useState(false);
  const { queryParams } = useProductionPlanQueryParams();
  const nodeFlags = useNodeBatchFlagsMapper(node);

  const { onNodeClick, mode } = useOnNodeClick({ node: node.tasks?.[0], batchNode: node, nodeFlags });
  const isSearchEmphasized = useProductionPlanSearchStore(isSearchEmphasizedSelector([node.id]));
  const shadowParams = useNodeShadowParams(node);

  const nodeMapper = useNodeBatchMapper();

  const isNodeDraggable = useIsGanttNodeDraggable(node);

  const { setNodeRef, attributes, listeners, isDragging } = useGanttDraggable({
    id: node.id,
    disabled: !isNodeDraggable,
    data: { id: Number(node.id), possibleQueues: [node.queueId], type: NodeType.BATCH },
  });

  const isWorkerMode = queryParams.mode === GanttMode.WORKER;

  const displayProductName = useProductionPlanSettings((state) => state.taskMainValue === TaskMainValueOptions.PRODUCT);
  const showOnTask = useProductionPlanSettings((state) => state.gantt.showOnTask);

  const { avatars, unassigned, hasAssignedWorkers } = GanttUtils.getGanttUnassigned({
    showOnTask,
    mode: queryParams.mode,
    softResourceAssignments: node.softResourceAssignments,
    requiredSoftResourcesAmount: node.requiredSoftResourcesAmount,
  });

  const { retoolingWidth, hasError, statusColor, nodeIds, nodeWidth, isSmallNode, showStatus } = nodeMapper(node);

  const handleClickOpen = (open: boolean) => {
    node.tasks?.[0] && (isWorkerMode || mode === 'PUBLICATION' || mode === 'FREEZING') ? onNodeClick() : setOpen(open);
  };

  const isSelected = isOpen || !!(queryParams.taskId && nodeIds.includes(queryParams.taskId));
  const isSearchSelected = useProductionPlanSearchStore(isSearchSelectedSelector(nodeIds));

  const tooltip = useCursorHoverState(isDragging || isOpen);

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={handleClickOpen}>
      <Popover.Root open={tooltip.open} onOpenChange={tooltip.onOpenChange}>
        <Popover.Anchor virtualRef={tooltip.virtualRef} />
        <DropdownMenu.Trigger>
          <NodeBase
            attributes={attributes}
            hasError={hasError}
            isDraggable={isNodeDraggable}
            isDragging={isDragging}
            isEmphasized={isSearchEmphasized}
            isOverdue={nodeFlags.overdue}
            isRetooling={nodeFlags.retooling}
            isSearchSelected={isSearchSelected}
            isSelected={isSelected}
            listeners={listeners}
            nodeIds={nodeIds}
            ref={setNodeRef as unknown as RefObject<HTMLDivElement>}
            retoolingWidth={retoolingWidth}
            shadowParams={shadowParams}
            toPublish={nodeFlags.toPublish}
            unassigned={unassigned}
            width={nodeWidth}
            onMouseEnter={tooltip.onMouseEnter}
            onMouseLeave={tooltip.onMouseLeave}
            onMouseMove={tooltip.onMouseMove}
          >
            <div className={clsx('flex w-full items-center gap-1')}>
              {statusColor && showStatus && <Status className="first-of-type:ml-1" color={statusColor} variant="dot" />}
              <NodeFlags className="first-of-type:ml-1" flags={nodeFlags} nodeId={node.id} />
              {node.tasks?.length && (
                <Badge
                  className={cn('bg-surface-container')}
                  StartIcon={<CUSTOM_ICONS.Merge className="text-icon-darker" />}
                >
                  {node.tasks.length}
                </Badge>
              )}

              {!isSmallNode && (
                <div className="overflow-hidden">
                  {node.tasks?.slice(0, 2).map((task) => (
                    <div className="truncate text-s11-sb text-nowrap" key={task.id}>
                      {displayProductName ? task.productName : task.name}
                    </div>
                  ))}
                </div>
              )}
              {(isWorkerMode || hasAssignedWorkers) && (
                <MultipleTableAvatar avatars={avatars} className="ml-auto pl-1" />
              )}
            </div>
            {children}
          </NodeBase>
        </DropdownMenu.Trigger>
        <Popover.Content side="bottom" sideOffset={16} onOpenAutoFocus={(event) => event.preventDefault()}>
          <NodeBatchPopoverContent
            endDate={node.endDate}
            flags={nodeFlags}
            id={node.id}
            showOnTask={showOnTask}
            startDate={node.startDate}
            status={node.status}
            tasks={(node.tasks ?? []).map((task) => ({
              id: task.id,
              name: displayProductName ? task.productName : task.name,
              total: task.progress.totalAmount.amount,
              unit: task.progress.totalAmount.unit.name,
              externalId: task.productionOrderDetails?.externalId,
              productionOrderNumber: task.productionOrderDetails?.number,
              dueDate: task.dueDate,
            }))}
          />
        </Popover.Content>
      </Popover.Root>
      <MultipleNodeDropdownMenuContent taskNodes={[node]} />
    </DropdownMenu.Root>
  );
});

NodeBatch.displayName = 'NodeBatch';

const useNodeBatchMapper = () => {
  const isSimulation = useIsGanttSimulation();
  return ({ tasks, nodeWidth, status }: QueueBatchNode) => {
    const firstTask = tasks?.[0];

    const frozen = firstTask && firstTask.frozen;
    const toPublish = firstTask && firstTask.toPublish;

    const { retoolingWidth } = GanttUtils.calculateRetooling(
      firstTask
        ? {
            startDate: new Date(firstTask.start),
            endDate: new Date(firstTask.end),
            setupEnd: firstTask.setupEnd,
            nodeWidth: nodeWidth,
          }
        : undefined,
    );

    const hasError = tasks?.some(({ errors }) => GanttUtils.hasCriticalError(errors));
    const statusColor = hasError ? 'red' : GanttUtils.getTaskStatusColor(status);
    const nodeIds = (tasks || []).map((node) => node.id);
    const isSmallNode = nodeWidth <= GanttConfig.SMALL_NODE_WIDTH;
    const showStatus = isSimulation
      ? !((frozen === true || toPublish === false) && status === TaskStatus.Pending)
      : true;

    return {
      retoolingWidth,
      hasError,
      statusColor,
      nodeWidth,
      nodeIds,
      isSmallNode,
      showStatus,
    };
  };
};
